import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Sprout, Package, ShieldCheck, Clock, TriangleAlert, Plus } from 'lucide-react';

import { formatRupeesCompact, UNIT_LABELS, type UnitOfMeasure } from '@sreerasa/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useFarmerProducts, useFarmerFarms } from '@/lib/queries';
import { useAuth } from '@/features/auth/auth-context';
import { supabase } from '@/lib/supabase';
import { transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/farmer/')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: '/auth/sign-in', search: { redirect: '/farmer' } });
    // Role isn't checked here — RLS is the real gate, and the JWT claim is
    // available in the component anyway. A non-farmer who forces this URL
    // sees an empty dashboard, which is the correct outcome.
  },
  component: FarmerDashboard,
});

const STATUS_META = {
  draft: { label: 'Draft', variant: 'neutral' as const, icon: Clock },
  pending_review: { label: 'Awaiting review', variant: 'warning' as const, icon: Clock },
  active: { label: 'Live', variant: 'success' as const, icon: ShieldCheck },
  out_of_stock: { label: 'Sold out', variant: 'neutral' as const, icon: Package },
  rejected: { label: 'Rejected', variant: 'danger' as const, icon: TriangleAlert },
  archived: { label: 'Archived', variant: 'neutral' as const, icon: Package },
};

function FarmerDashboard() {
  const { isFarmer } = useAuth();
  const { data: products, isLoading } = useFarmerProducts();
  const { data: farms } = useFarmerFarms();

  if (!isFarmer) {
    return (
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <Sprout className="mx-auto size-10 text-soil-300" aria-hidden />
        <h1 className="mt-4 font-display text-2xl font-bold text-soil-900">Farmers only</h1>
        <p className="mt-2 text-soil-600">
          This is the farmer dashboard. If you'd like to sell with us, write to
          sreerasaproducts@gmail.com and we'll get you set up.
        </p>
        <Link to="/shop" className="mt-6 inline-block">
          <Button>Back to the shop</Button>
        </Link>
      </div>
    );
  }

  const live = products?.filter((p) => p.status === 'active').length ?? 0;
  const pending = products?.filter((p) => p.status === 'pending_review').length ?? 0;
  const verifiedFarms = farms?.filter((f) => f.verification === 'verified').length ?? 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-display font-black text-soil-900">Your farm</h1>
          <p className="mt-2 text-soil-600">Your listings, your prices, your name on every one.</p>
        </div>
        <Button disabled title="Listing creation lands in the next release">
          <Plus aria-hidden />
          New listing
        </Button>
      </div>

      {/* ── Stats ── */}
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[
          {
            label: 'Live listings',
            value: live,
            icon: ShieldCheck,
            tone: 'text-farm-600 bg-farm-50',
          },
          {
            label: 'Awaiting review',
            value: pending,
            icon: Clock,
            tone: 'text-harvest-700 bg-harvest-100',
          },
          {
            label: 'Verified farms',
            value: verifiedFarms,
            icon: Sprout,
            tone: 'text-sun-600 bg-sun-300/20',
          },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...transitions.base, delay: index * 0.06 }}
            className="rounded-card border border-soil-200 bg-white p-5"
          >
            <div className={`flex size-9 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="size-4" aria-hidden />
            </div>
            <p className="mt-3 font-display text-3xl font-black text-soil-900">{stat.value}</p>
            <p className="text-sm text-soil-500">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ── No farm yet ── */}
      {farms && farms.length === 0 && (
        <div className="mt-8 rounded-card border-2 border-dashed border-harvest-300 bg-harvest-100/50 p-8 text-center">
          <Sprout className="mx-auto size-9 text-harvest-600" aria-hidden />
          <h2 className="mt-3 font-display text-lg font-bold text-soil-900">Add your farm first</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-soil-600">
            Before you can list produce, we need to know where it grows — the village, the district,
            how you farm. Then we verify it. That verification is what lets us put a name on every
            listing and mean it.
          </p>
          <p className="mt-4 text-sm">
            <a
              href="mailto:sreerasaproducts@gmail.com"
              className="font-semibold text-farm-700 hover:underline"
            >
              Write to us to get started
            </a>
          </p>
        </div>
      )}

      {/* ── Listings ── */}
      <h2 className="mt-10 font-display text-xl font-bold text-soil-900">Your listings</h2>

      {isLoading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-24 rounded-card" />
          ))}
        </div>
      ) : products && products.length > 0 ? (
        <div className="mt-4 space-y-3">
          {products.map((product, index) => {
            const meta =
              STATUS_META[product.status as keyof typeof STATUS_META] ?? STATUS_META.draft;
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...transitions.base, delay: index * 0.04 }}
                className="flex flex-wrap items-center gap-4 rounded-card border border-soil-200 bg-white p-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display font-bold text-soil-900">{product.title}</p>
                    <Badge variant={meta.variant} size="sm">
                      <meta.icon aria-hidden />
                      {meta.label}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-soil-500">
                    {formatRupeesCompact(product.price_paise)} per{' '}
                    {UNIT_LABELS[product.unit as UnitOfMeasure] ?? product.unit} ·{' '}
                    {product.stock_qty} in stock
                  </p>

                  {product.status === 'rejected' && product.rejection_reason && (
                    <p className="mt-2 rounded-lg bg-red-50 p-2.5 text-xs text-danger">
                      <span className="font-semibold">Why: </span>
                      {product.rejection_reason}
                    </p>
                  )}
                </div>

                {product.status === 'active' && (
                  <Link to="/shop/$slug" params={{ slug: product.slug }}>
                    <Button size="sm" variant="outline">
                      View listing
                    </Button>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 rounded-card border border-dashed border-soil-300 bg-white/50 p-12 text-center">
          <Package className="mx-auto size-8 text-soil-300" aria-hidden />
          <p className="mt-3 font-display font-bold text-soil-800">No listings yet</p>
          <p className="mt-1 text-sm text-soil-500">
            Once your farm is verified, your produce goes here.
          </p>
        </div>
      )}
    </div>
  );
}
